import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useCachedApi } from '../hooks/useCachedApi';
import { parseApiError } from '../utils/errorUtils';
import { createPersona } from '../services/apiService';

import { WIZARD_WORKFLOWS } from '../content/personaContent';
import TypeSelectionStep from '../components/initializer/TypeSelectionStep';
import CreateFromTextStep from '../components/initializer/CreateFromTextStep';
import CoreInfoStep from '../components/initializer/CoreInfoStep';
import StyleFormStep from '../components/initializer/StyleFormStep';
import SubstanceFormStep from '../components/initializer/SubstanceFormStep';
import ExpertiseFormStep from '../components/initializer/ExpertiseFormStep';
import IpipNeoTestStep from '../components/initializer/IpipNeoTestStep';
import SlidersStep from '../components/initializer/SlidersStep';
import ReviewStep from '../components/initializer/ReviewStep';

function PersonaInitializerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const initialPersonaData = location.state?.personaData;

  const [personaData, setPersonaData] = useState(initialPersonaData || {});
  const [imagePreview, setImagePreview] = useState(null);
  const [workflow, setWorkflow] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);

  const { data: options, loading: optionsLoading } = useCachedApi(
    '/v1/personas/config/options',
    600000
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (initialPersonaData) {
      const newWorkflow =
        WIZARD_WORKFLOWS[initialPersonaData.type] ||
        WIZARD_WORKFLOWS['default'];
      setWorkflow(newWorkflow);
      setStepIndex(0);
    }
  }, [initialPersonaData]);

  const startManualWizard = (type) => {
    const newWorkflow = WIZARD_WORKFLOWS[type] || WIZARD_WORKFLOWS['default'];
    setWorkflow(newWorkflow);
    setPersonaData({ type: type });
    setStepIndex(0);
  };

  const startTextWizard = (type) => {
    const newWorkflow = ['createText', 'review'];
    setWorkflow(newWorkflow);
    setPersonaData({ type: type });
    setStepIndex(0);
  };

  const handleNextStep = () => setStepIndex((prev) => prev + 1);
  const handlePrevStep = () => setStepIndex((prev) => prev - 1);

  const handleAnalysisComplete = (generatedData) => {
    setPersonaData((prevData) => ({ ...prevData, ...generatedData }));
    handleNextStep();
  };

  const handleSavePersona = async (finalPersonaData) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const accessToken = await getAccessTokenSilently();

      await createPersona(finalPersonaData, accessToken);
      navigate('/profiles');
    } catch (err) {
      const formattedError = parseApiError(err);
      setSaveError(formattedError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadSuccess = ({ finalImageUrl, previewUrl }) => {
    setPersonaData((prev) => ({ ...prev, imageUrl: finalImageUrl }));
    setImagePreview(previewUrl);
  };

  const renderCurrentStep = () => {
    if (optionsLoading) return <p>Loading configuration...</p>;

    const commonProps = {
      personaData,
      setPersonaData,
      onNext: handleNextStep,
      onBack: handlePrevStep,
      currentStepNumber: stepIndex + 1,
      totalSteps: workflow.length,
    };

    if (workflow.length === 0) {
      return (
        <TypeSelectionStep
          onManualSelect={startManualWizard}
          onTextSelect={startTextWizard}
          options={options}
          hideDigitalLegacy={true}
        />
      );
    }

    const currentStepName = workflow[stepIndex];

    switch (currentStepName) {
      case 'coreInfo':
        return (
          <CoreInfoStep
            {...commonProps}
            options={options}
            onUploadSuccess={handleUploadSuccess}
            onBack={() => setWorkflow([])}
          />
        );
      case 'styleForm':
        return <StyleFormStep {...commonProps} options={options} />;
      case 'substanceForm':
        return <SubstanceFormStep {...commonProps} />;
      case 'expertiseForm':
        return <ExpertiseFormStep {...commonProps} />;
      case 'ipipNeoTest':
        return <IpipNeoTestStep {...commonProps} />;
      case 'sliders':
        return <SlidersStep {...commonProps} />;
      case 'createText':
        return (
          <CreateFromTextStep
            {...commonProps}
            onUploadSuccess={handleUploadSuccess}
            onAnalysisComplete={handleAnalysisComplete}
            onBack={() => setWorkflow([])}
          />
        );
      case 'review':
        return (
          <ReviewStep
            {...commonProps}
            imagePreview={imagePreview}
            onSave={handleSavePersona}
            isSaving={isSaving}
            saveError={saveError}
          />
        );
      default:
        return <p>Error: Wizard step not found.</p>;
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Create a New Persona</h2>
      </div>
      {renderCurrentStep()}
    </div>
  );
}

export default withAuthenticationRequired(PersonaInitializerPage);
